import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  Typography,
  Divider,
  Button,
  Collapse,
  Switch,
  Space,
  Row,
  Col,
  Badge,
} from 'antd';
import { useApiVersion } from '../../../context/ApiVersionContext';
import {
  API_VERSIONS,
  getEffectiveVersion,
} from '../../../utils/apiVersionManager';
import { isNonProdEnvironment } from '../../../envConfig';
import { ENDPOINT_GROUPS } from '../../../utils/endpointConfig';
import './FeatureToggleModal.less';

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;

const getGroupsWithV2Endpoints = (overrideMap, availableGroups) => {
  return availableGroups
    .filter((group) =>
      group.endpoints.some(
        (endpoint) =>
          getEffectiveVersion(endpoint, overrideMap) === API_VERSIONS.V2
      )
    )
    .map((group) => group.id);
};

const getV2CountForGroup = (group, overrideMap) => {
  return group.endpoints.filter(
    (endpoint) => getEffectiveVersion(endpoint, overrideMap) === API_VERSIONS.V2
  ).length;
};

function FeatureToggleModal({ visible, onClose }) {
  const { overrides, updateEndpointOverride } = useApiVersion();
  const [pendingOverrides, setPendingOverrides] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [initialActiveKeys, setInitialActiveKeys] = useState([]);
  const [collapseKey, setCollapseKey] = useState(0);

  // No filtering - show all endpoints
  const allEndpointGroups = ENDPOINT_GROUPS;

  useEffect(() => {
    if (visible) {
      setPendingOverrides({ ...overrides });
      setHasChanges(false);
      // Expand groups that have any v2 endpoints (default or overridden)
      setInitialActiveKeys(
        getGroupsWithV2Endpoints(overrides, ENDPOINT_GROUPS)
      );
      setCollapseKey((prev) => prev + 1);
    }
  }, [visible, overrides]);

  const bulkStats = useMemo(() => {
    const total = allEndpointGroups.reduce(
      (sum, g) => sum + g.endpoints.length,
      0
    );
    const v2 = allEndpointGroups.reduce(
      (sum, g) => sum + getV2CountForGroup(g, pendingOverrides),
      0
    );
    return {
      total,
      v2,
      v1: total - v2,
      v2Pct: Math.round((v2 / total) * 100),
      isAllV1: v2 === 0,
      isAllV2: v2 === total,
    };
  }, [allEndpointGroups, pendingOverrides]);

  if (!isNonProdEnvironment()) {
    return null;
  }

  const handleToggle = (endpoint, checked) => {
    const newOverrides = { ...pendingOverrides };
    const targetVersion = checked ? API_VERSIONS.V2 : API_VERSIONS.V1;

    // If toggling to the default version, remove override
    if (targetVersion === endpoint.version) {
      delete newOverrides[endpoint.id];
    } else {
      newOverrides[endpoint.id] = targetVersion;
    }

    setPendingOverrides(newOverrides);
    setHasChanges(true);
  };

  const handleSwitchAll = (targetVersion) => {
    const newOverrides = {};
    allEndpointGroups.forEach((group) => {
      group.endpoints.forEach((endpoint) => {
        const defaultVersion = endpoint.version || API_VERSIONS.V1;
        if (defaultVersion !== targetVersion) {
          newOverrides[endpoint.id] = targetVersion;
        }
      });
    });
    setPendingOverrides(newOverrides);
    setHasChanges(true);
  };

  const handleReset = () => {
    setPendingOverrides({});
    setHasChanges(true);
  };

  const handleSave = () => {
    // Get all unique keys from both objects
    const allKeys = new Set([
      ...Object.keys(overrides),
      ...Object.keys(pendingOverrides),
    ]);

    // Single iteration to update all changes
    allKeys.forEach((key) => {
      const oldValue = overrides[key];
      const newValue = pendingOverrides[key];

      if (oldValue !== newValue) {
        updateEndpointOverride(key, newValue || API_VERSIONS.DEFAULT);
      }
    });

    onClose();
  };

  const handleCancel = () => {
    setHasChanges(false);
    onClose();
  };

  if (!visible) {
    return null;
  }

  return (
    <Modal
      title={
        <Title level={4} style={{ margin: 0 }}>
          API Version Manager
        </Title>
      }
      visible={visible}
      onCancel={handleCancel}
      footer={[
        <Button key="reset" onClick={handleReset}>
          Reset All
        </Button>,
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          onClick={handleSave}
          disabled={!hasChanges}
        >
          Save Changes
        </Button>,
      ]}
      width={800}
      className="feature-toggle-modal"
      bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
    >
      <Paragraph type="secondary">
        Toggle specific endpoints to use the V2 API. Changes are applied only
        after clicking "Save".
      </Paragraph>

      <Divider />

      <div className="bulk-switch-panel">
        <Text
          strong
          style={{ fontSize: 14, display: 'block', marginBottom: 14 }}
        >
          Switch all {bulkStats.total} endpoints
        </Text>

        <div className="version-progress">
          <div className="version-progress-labels">
            <span
              className={`version-label ${!bulkStats.isAllV2 ? 'active' : ''}`}
            >
              V1 &middot; {bulkStats.v1}
            </span>
            <span
              className={`version-label ${
                !bulkStats.isAllV1 ? 'active v2' : ''
              }`}
            >
              {bulkStats.v2} &middot; V2
            </span>
          </div>
          <div className="version-progress-track">
            <div
              className="version-progress-fill"
              style={{ width: `${bulkStats.v2Pct}%` }}
            />
          </div>
        </div>

        <Row gutter={12} style={{ marginTop: 16 }}>
          <Col span={12}>
            <Button
              block
              size="large"
              className={`bulk-btn bulk-btn-v1 ${
                bulkStats.isAllV1 ? 'is-active' : ''
              }`}
              onClick={() => handleSwitchAll(API_VERSIONS.V1)}
            >
              All V1
            </Button>
          </Col>
          <Col span={12}>
            <Button
              block
              size="large"
              className={`bulk-btn bulk-btn-v2 ${
                bulkStats.isAllV2 ? 'is-active' : ''
              }`}
              onClick={() => handleSwitchAll(API_VERSIONS.V2)}
            >
              All V2
            </Button>
          </Col>
        </Row>
      </div>

      <Collapse
        key={collapseKey}
        defaultActiveKey={initialActiveKeys}
        style={{ marginTop: 20 }}
      >
        {allEndpointGroups.map((group) => {
          const v2Count = getV2CountForGroup(group, pendingOverrides);
          const panelHeader = (
            <Space>
              <span>{group.title}</span>
              <Badge
                count={v2Count}
                showZero
                style={{
                  backgroundColor: v2Count > 0 ? '#1890ff' : '#d9d9d9',
                }}
              />
            </Space>
          );
          return (
            <Panel header={panelHeader} key={group.id}>
              <Space direction="vertical" style={{ width: '100%' }}>
                {group.endpoints.map((endpoint) => {
                  const effectiveVersion = getEffectiveVersion(
                    endpoint,
                    pendingOverrides
                  );
                  const isV2 = effectiveVersion === API_VERSIONS.V2;
                  const isOverridden = !!pendingOverrides[endpoint.id];
                  const statusLabel = isOverridden
                    ? ' (overridden)'
                    : ` (default: ${endpoint.version || 'v1'})`;

                  return (
                    <div key={endpoint.id} style={{ marginBottom: 16 }}>
                      <Row
                        justify="space-between"
                        align="middle"
                        style={{
                          padding: '8px 0',
                          borderBottom: '1px solid #f0f0f0',
                        }}
                      >
                        <Col span={18}>
                          <Text strong>{endpoint.label}</Text>
                          <br />
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {endpoint.id}
                            {statusLabel}
                          </Text>
                        </Col>
                        <Col>
                          <Switch
                            checkedChildren="v2"
                            unCheckedChildren="v1"
                            checked={isV2}
                            onChange={(checked) =>
                              handleToggle(endpoint, checked)
                            }
                          />
                        </Col>
                      </Row>
                      {endpoint.urlPatterns &&
                        endpoint.urlPatterns.length > 0 && (
                          <div style={{ paddingLeft: 24, paddingTop: 8 }}>
                            <Text
                              type="secondary"
                              style={{ fontSize: 11, fontStyle: 'italic' }}
                            >
                              Includes {endpoint.urlPatterns.length} URL
                              {endpoint.urlPatterns.length > 1 ? 's' : ''}:
                            </Text>
                            <ul style={{ margin: '4px 0', paddingLeft: 20 }}>
                              {endpoint.urlPatterns.map((pattern, idx) => {
                                let displayText = 'Unknown pattern';

                                if (typeof pattern === 'string') {
                                  // String pattern - show last segment
                                  displayText =
                                    pattern.split('/').pop() || pattern;
                                } else if (pattern.base) {
                                  // Object pattern with base
                                  if (typeof pattern.base === 'string') {
                                    // Base is a string - show last segment
                                    displayText = `${
                                      pattern.base.split('/').pop() ||
                                      pattern.base
                                    }${pattern.dynamic ? ' (dynamic)' : ''}`;
                                  } else if (
                                    typeof pattern.base === 'function'
                                  ) {
                                    // Base is a function - show function name or generic label
                                    const funcName =
                                      pattern.base.name || 'Dynamic URL';
                                    displayText = `${funcName} (dynamic)`;
                                  }
                                }

                                return (
                                  <li key={idx}>
                                    <Text
                                      type="secondary"
                                      style={{ fontSize: 11 }}
                                    >
                                      {displayText}
                                    </Text>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        )}
                    </div>
                  );
                })}
              </Space>
            </Panel>
          );
        })}
      </Collapse>

      <div className="feature-info" style={{ marginTop: 20 }}>
        <Text type="warning" style={{ fontSize: 12 }}>
          Note: Changes apply immediately after saving and will persist across
          page refreshes.
        </Text>
      </div>
    </Modal>
  );
}

export default FeatureToggleModal;
